import JSZip from 'jszip';
import { LoaderContextValue } from 'src/context/LoaderContext';

interface RequestItem {
  filename: string;
  url: string;
  path?: string;
}

type DownloadZipLoader = Pick<LoaderContextValue, 'show' | 'updateProgress'>;

const CHUNK_SIZE = 50;

const chunkArray = <T>(items: T[], size: number): T[][] => {
  const chunks: T[][] = [];

  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }

  return chunks;
};

const fetchZipChunk = async (chunk: RequestItem[]): Promise<Blob> => {
  const response = await fetch('/api/download-zip', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(chunk),
  });

  if (!response.ok) {
    throw new Error(`Failed to download zip chunk (status ${response.status})`);
  }

  return response.blob();
};

export const downloadZip = async (
  request: RequestItem[],
  filename = 'kb_archive.zip',
  loader?: DownloadZipLoader,
): Promise<Blob | Error> => {
  try {
    loader?.show({ progress: true });

    const chunks = chunkArray(request, CHUNK_SIZE);

    // Send at most CHUNK_SIZE files per request, one request at a time.
    const chunkBlobs: Blob[] = [];

    for (let index = 0; index < chunks.length; index++) {
      chunkBlobs.push(await fetchZipChunk(chunks[index]));
      loader?.updateProgress(Math.round(((index + 1) / chunks.length) * 100));
    }

    const mergedZip = new JSZip();

    for (const chunkBlob of chunkBlobs) {
      const chunkZip = await JSZip.loadAsync(chunkBlob);

      await Promise.all(
        Object.keys(chunkZip.files).map(async (entryName) => {
          const entry = chunkZip.files[entryName];

          if (entry.dir) return;

          mergedZip.file(entryName, await entry.async('uint8array'));
        }),
      );
    }

    const finalBlob = await mergedZip.generateAsync({ type: 'blob' });

    // Create a temporary local URL for the object blob
    const downloadUrl = globalThis.URL.createObjectURL(finalBlob);

    // Create a hidden anchor element and simulate a click
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', filename); // Force download name

    document.body.appendChild(link);
    link.click(); // Trigger browser download

    // Clean up the DOM and release memory
    link.remove();
    globalThis.URL.revokeObjectURL(downloadUrl);

    return finalBlob;
  } catch (error) {
    console.log(error);

    // Failures here (fetch, a chunk's status, JSZip) are Errors; they are returned, not thrown.
    return error as Error;
  }
};
